/**
 * Select a Destination Project.
 */
// @todo: Migrate to 'Select' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Select} from '@wandb/weave/components/Form/Select';
// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {IconFolderAlt as IconFolderAltComp} from '@wandb/weave/components/Icon';
import {debounce} from 'lodash';
import React, {useCallback, useMemo} from 'react';
import {type FormatOptionLabelMeta, type InputActionMeta} from 'react-select';
// eslint-disable-next-line wandb/no-deprecated-imports
import styled from 'styled-components';

import * as S from './SelectComponents.styles';

export type ProjectOption = {
  value: string;
  entityName: string;
  projectId: string;
  projectName: string;
};

export type EntityOptions = {
  label: string;
  options: ProjectOption[];
};

type SelectProjectProps = {
  options: EntityOptions[];
  defaultEntityName: string;
  defaultProjectName: string;
  onSelectProject: (option: ProjectOption) => void;
  setProjectSearchString?: (searchString: string) => void;
  onMenuClose?: () => void;
  errorState?: boolean;
  loading?: boolean;
};

export const IconFolderAlt = styled(IconFolderAltComp)`
  margin-right: 8px;
  flex: 0 0 auto;
`;

// Show the entity name as a prefix for the selected project to disambiguate.
const OptionLabel = (
  props: ProjectOption,
  context: FormatOptionLabelMeta<ProjectOption>
) => {
  const {entityName, projectName} = props;
  const selected = context.selectValue[0];
  const isSelected = selected === props;
  if (isSelected) {
    return (
      <S.SelectOptionLabel>
        <S.SelectOptionLabelText>
          {entityName}/{projectName}
        </S.SelectOptionLabelText>
      </S.SelectOptionLabel>
    );
  }
  return (
    <S.SelectOptionLabel>
      <IconFolderAlt />
      <S.SelectOptionLabelText>{projectName}</S.SelectOptionLabelText>
    </S.SelectOptionLabel>
  );
};

export const SelectProject = ({
  options,
  defaultEntityName,
  defaultProjectName,
  onSelectProject,
  setProjectSearchString,
  onMenuClose,
  errorState,
  loading,
}: SelectProjectProps) => {
  const onChange = (option: ProjectOption | null) => {
    if (option != null) {
      onSelectProject(option);
    }
  };

  const debouncedSearch = useMemo(
    () =>
      setProjectSearchString
        ? debounce((searchString: string) => {
            setProjectSearchString(searchString);
          }, 300) // 300ms delay
        : undefined,
    [setProjectSearchString]
  );

  // Handle input changes for search
  const onInputChange = useCallback(
    (newValue: string, actionMeta: InputActionMeta) => {
      if (actionMeta.action === 'input-change' && debouncedSearch) {
        debouncedSearch(newValue);
      }
    },
    [debouncedSearch]
  );

  let defaultOption;
  for (const optionGroup of options) {
    if (defaultOption) {
      break;
    }
    for (const option of optionGroup.options) {
      if (
        option.entityName === defaultEntityName &&
        option.projectName === defaultProjectName
      ) {
        defaultOption = option;
        break;
      }
    }
  }

  return (
    <Select<ProjectOption, false, EntityOptions>
      isLoading={loading}
      options={options}
      id="select-project"
      defaultValue={defaultOption}
      formatOptionLabel={OptionLabel}
      placeholder="Select a project"
      onChange={onChange}
      onInputChange={onInputChange}
      onMenuClose={onMenuClose}
      errorState={errorState}
    />
  );
};
